/**
 * Route map shared by the Worker and the SPA.
 * Keep this in sync with apps/api/src/routes. The SPA imports it so a typo
 * in a path is a compile error, not a 404 in production.
 */
export const routes = {
  health: '/health',
  event: (slug: string) => `/events/${slug}` as const,
  vouchersValidate: '/vouchers/validate',
  checkout: '/checkout',
  order: (orderId: string) => `/orders/${orderId}` as const,
  orderSession: (orderId: string) => `/orders/${orderId}/session` as const,
  orderResendConfirmation: (orderId: string) => `/orders/${orderId}/resend-confirmation` as const,
  authRequest: '/auth/magic-link',
  authVerify: '/auth/verify',
  authLogout: '/auth/logout',
  me: '/me',
  meProfile: '/me/profile',
  meRegistrations: '/me/registrations',
  meOrders: '/me/orders',
  meAvailableFormats: '/me/available-formats',
  meReferral: '/me/referral',
  meInvites: '/me/referral/invites',
  meTeams: '/me/teams',
  meTeam: (teamId: string) => `/me/teams/${teamId}` as const,
  meTeamMembers: (teamId: string) => `/me/teams/${teamId}/members` as const,
  meTeamMember: (teamId: string, memberId: string) =>
    `/me/teams/${teamId}/members/${memberId}` as const,
  meTeamMemberResend: (teamId: string, memberId: string) =>
    `/me/teams/${teamId}/members/${memberId}/resend` as const,
  teamsJoin: (code: string) => `/teams/join/${encodeURIComponent(code)}` as const,
  /** P-12: presigned PUT to R2, confirm the upload, presigned GET (owner only). */
  documentsUploadUrl: '/me/documents/upload-url',
  documentComplete: (id: string) => `/me/documents/${id}/complete` as const,
  documentUrl: (id: string) => `/me/documents/${id}/url` as const,
  /**
   * Signed PUT/GET through the Worker when R2 S3 keys are not configured (local dev): the
   * short-lived signature in the query is the credential, like an R2 presigned URL.
   */
  documentFile: (id: string) => `/documents/files/${id}` as const,
  documents: '/me/documents',
  adminMe: '/admin/me',
  adminDashboard: '/admin/dashboard',
  adminRegistrations: '/admin/registrations',
  adminRegistration: (id: string) => `/admin/registrations/${id}` as const,
  adminRegistrationNotes: (id: string) => `/admin/registrations/${id}/notes` as const,
  adminExport: '/admin/export.csv',
  /** A-05 review queue: certificates and licences waiting for review, oldest first. */
  adminDocuments: '/admin/documents',
  /** Certificate review (A-05): presigned GET and approve/reject. */
  adminDocumentUrl: (id: string) => `/admin/documents/${id}/url` as const,
  adminDocumentReview: (id: string) => `/admin/documents/${id}/review` as const,
  /** Licence review (A-05): a licence counts only once approved, like a certificate. */
  adminLicenceReview: (userId: string) => `/admin/users/${userId}/licence/review` as const,
  adminEvent: '/admin/event',
  adminInsuranceStatements: '/admin/insurance/statements',
  adminFormats: '/admin/formats',
  adminFormat: (id: string) => `/admin/formats/${id}` as const,
  adminFormatTiers: (id: string) => `/admin/formats/${id}/tiers` as const,
  adminFormatTier: (id: string, tierId: string) => `/admin/formats/${id}/tiers/${tierId}` as const,
  adminFormatTierReplace: (id: string, tierId: string) =>
    `/admin/formats/${id}/tiers/${tierId}/replace` as const,
  adminVouchers: '/admin/vouchers',
  adminVoucher: (id: string) => `/admin/vouchers/${id}` as const,
  adminTeams: '/admin/teams',
  adminTeam: (id: string) => `/admin/teams/${id}` as const,
  adminTeamCaptain: (id: string) => `/admin/teams/${id}/captain` as const,
  adminTeamCancel: (id: string) => `/admin/teams/${id}/cancel` as const,
  adminTeamReopen: (id: string) => `/admin/teams/${id}/reopen` as const,
  adminTeamMemberMove: (id: string, memberId: string) =>
    `/admin/teams/${id}/members/${memberId}/move` as const,
  adminTeamMemberReplace: (id: string, memberId: string) =>
    `/admin/teams/${id}/members/${memberId}/replace` as const,
  adminTeamMemberResend: (id: string, memberId: string) =>
    `/admin/teams/${id}/members/${memberId}/resend` as const,
  adminMcpConnections: '/admin/mcp/connections',
  adminMcpConnection: (id: string) => `/admin/mcp/connections/${id}` as const,
  /** Back-office MCP server (issue #47): Streamable HTTP, Bearer tokens from `/oauth/token`. */
  mcp: '/mcp',
  oauthProtectedResource: '/.well-known/oauth-protected-resource',
  oauthAuthorizationServer: '/.well-known/oauth-authorization-server',
  oauthRegister: '/oauth/register',
  oauthAuthorize: '/oauth/authorize',
  oauthToken: '/oauth/token',
  oauthRevoke: '/oauth/revoke',
  /** The pending authorization behind the SPA consent page (cookie session). */
  oauthRequest: (id: string) => `/oauth/requests/${id}` as const,
  oauthRequestApprove: (id: string) => `/oauth/requests/${id}/approve` as const,
  oauthRequestDeny: (id: string) => `/oauth/requests/${id}/deny` as const,
  stripeWebhook: '/webhooks/stripe',
  brevoWebhook: '/webhooks/brevo',
} as const;

export const SESSION_COOKIE = 'gg_session';
export const SESSION_TTL_SECONDS = 60 * 60 * 24 * 30; // 30 days
export const MAGIC_LINK_TTL_SECONDS = 60 * 15;
/** Links in the confirmation email, often opened later: 7 days, single use (record 0026, D1). */
export const ORDER_MAGIC_LINK_TTL_SECONDS = 60 * 60 * 24 * 7;
/** "Resend the email" on the confirmation screen, per order (record 0026, D5). */
export const CONFIRMATION_RESENDS_PER_HOUR = 3;
export const CHECKOUT_HOLD_SECONDS = 60 * 20;
